// "Parking" persistence for in-progress documents (factura/albarán/presupuesto/pedido).
// Saves the editor's in-memory state to localStorage so that if the user leaves the
// page before saving, the last state is there when they come back. Each saved draft
// is timestamped so the notification bell can list "forms in progress".
//
// Each editing session owns a unique *instance* id, so parking several different
// facturas / presupuestos at once keeps them as separate entries in the bell
// instead of overwriting one another. Legacy keys without an instance id (parked
// before this scheme) are still listed and cleanable.
const PREFIX = 'zcom_doc_draft:';

// Keep at most this many parked drafts per user, evicting the oldest — so
// abandoned in-progress documents can accumulate (the point of multi-parking)
// without ever growing localStorage without bound.
const DRAFT_CAP = 30;

// A fresh instance id for a new editing session. Prefer the crypto UUID (https),
// fall back to time+random for older/insecure contexts.
export function newDraftInstanceId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// Drop the oldest drafts for `uid` beyond the cap so parked forms stay bounded.
function pruneDrafts(uid) {
  if (!uid) return;
  try {
    const mine = [];
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(PREFIX)) continue;
      if (fullKey.slice(PREFIX.length).split(':')[1] !== uid) continue;
      let savedAt = 0;
      try { savedAt = JSON.parse(localStorage.getItem(fullKey))?._savedAt || 0; } catch { savedAt = 0; }
      mine.push({ fullKey, savedAt });
    }
    if (mine.length <= DRAFT_CAP) return;
    mine.sort((a, b) => a.savedAt - b.savedAt); // oldest first
    for (const m of mine.slice(0, mine.length - DRAFT_CAP)) localStorage.removeItem(m.fullKey);
  } catch {
    // ignore
  }
}

// Remove the single oldest parked draft for `uid` other than `exceptFullKey`,
// so a write that just hit the quota can free space and keep the CURRENT draft
// instead of silently keeping the older, smaller one. Returns true if it evicted
// something (i.e. it's worth retrying the write).
function evictOldestOtherDraft(uid, exceptFullKey) {
  if (!uid) return false;
  try {
    let oldest = null;
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(PREFIX)) continue;
      if (fullKey === exceptFullKey) continue;
      if (fullKey.slice(PREFIX.length).split(':')[1] !== uid) continue;
      let savedAt = 0;
      try { savedAt = JSON.parse(localStorage.getItem(fullKey))?._savedAt || 0; } catch { savedAt = 0; }
      if (!oldest || savedAt < oldest.savedAt) oldest = { fullKey, savedAt };
    }
    if (oldest) { localStorage.removeItem(oldest.fullKey); return true; }
  } catch {
    // ignore
  }
  return false;
}

// Persist a draft. Returns true on success. Critically, a quota failure must NOT
// silently keep the previous (smaller) snapshot — that is exactly how a parked
// document "comes back not full". On failure we evict OTHER (older) drafts to
// make room and retry, so the document being edited right now always wins the
// space race.
export function saveDocumentDraft(key, data) {
  if (!key) return false;
  const fullKey = PREFIX + key;
  const uid = key.split(':')[1];
  const serialized = JSON.stringify({ ...data, _savedAt: Date.now() });
  for (let attempt = 0; attempt < DRAFT_CAP + 2; attempt++) {
    try {
      localStorage.setItem(fullKey, serialized);
      pruneDrafts(uid);
      return true;
    } catch {
      // Out of space (or private mode). Free the oldest OTHER draft and retry;
      // if there's nothing left to evict, give up rather than loop forever.
      if (!evictOldestOtherDraft(uid, fullKey)) return false;
    }
  }
  return false;
}

export function loadDocumentDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearDocumentDraft(key) {
  if (!key) return;
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    // ignore
  }
}

// Map a parsed draft to the route that reopens its editor, carrying the instance
// id so the create page resumes exactly this parked draft (and not a fresh one).
function resumePathFor(family, docType, instanceId) {
  let base;
  if (family === 'estimate') base = '/estimates/new';
  else if (family === 'order') base = '/orders/new';
  else if (docType === 'delivery_note') base = '/invoices/new?docType=delivery_note';
  else if (docType === 'proforma') base = '/invoices/new?docType=proforma';
  else if (docType === 'simplified') base = '/invoices/new?docType=simplified';
  else base = '/invoices/new';
  if (instanceId) base += (base.includes('?') ? '&' : '?') + 'draft=' + encodeURIComponent(instanceId);
  return base;
}

// i18n kind key (see locales: draftGuard.kind_*) for a parsed draft.
function kindKeyFor(family, docType) {
  if (family === 'estimate') return 'estimate';
  if (family === 'order') return 'order';
  if (docType === 'delivery_note') return 'delivery_note';
  if (docType === 'proforma') return 'proforma';
  if (docType === 'simplified') return 'simplified';
  return 'invoice';
}

// All parked drafts belonging to a user, newest first. Used by the notification
// bell / activity feed to show "you have a form in progress" reminders.
// `clientNameById` (optional) lets the bell recover a client name for older
// drafts that stored only the client id (parked before the name was captured,
// or before the name was displayed) — the stored `_clientName` still wins.
export function listDocumentDrafts(userId, clientNameById = null) {
  if (!userId) return [];
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(PREFIX)) continue;
      // Key shape: "<family>:<uid>:<docType>:<instanceId>" for the invoice family,
      // "<family>:<uid>:<instanceId>" for estimate/order. Legacy keys omit the
      // instance id (and, for the invoice family only, keep the docType segment).
      const suffix = fullKey.slice(PREFIX.length);
      const parts = suffix.split(':');
      const family = parts[0];                          // invoice | estimate | order
      let uid, docType, instanceId;
      if (family === 'invoice') { uid = parts[1]; docType = parts[2] || 'invoice'; instanceId = parts[3] || ''; }
      else { uid = parts[1]; docType = family; instanceId = parts[2] || ''; }
      if (uid !== userId) continue;

      let payload = null;
      try { payload = JSON.parse(localStorage.getItem(fullKey)); } catch { payload = null; }
      if (!payload) continue;

      const resolvedName = payload._clientName
        || (clientNameById && payload.selectedClientId ? clientNameById[payload.selectedClientId] : null)
        || null;

      out.push({
        storageKey: suffix,
        family,
        docType,
        instanceId,
        savedAt: payload._savedAt || 0,
        clientName: resolvedName,
        resumePath: resumePathFor(family, docType, instanceId),
        kindKey: kindKeyFor(family, docType),
      });
    }
  } catch {
    // ignore
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}
